import { useEffect, useState, useMemo, useCallback } from 'react';
import { useOrganizationContext } from '../service/context/organizationContext';
import {
  DeletedHarmReport,
  DeletedHarmReportsFilter,
  DeletedHarmReportsKebabOption,
} from '../pages/deletedHarmReports/deletedHarmReports.type';
import { ReportService } from '../service/reportService';
import { toastV2 } from '../component/toastV2/toastV2';
import { useTranslation } from 'react-i18next';
import {
  GetHarmReportsResponse,
  HarmReportsFilter,
} from '../pages/harmReports/harmReports.type';
import { GetReportsByFilterRequest } from '@inplay/models/dist/src/getReportsByFilterRequest';
import { ReportStatus } from '@inplay/models';
import { useNavigate } from 'react-router-dom';

export const useDeletedHarmReports = () => {
  const navigate = useNavigate();
  const { selectedVenueBranch } = useOrganizationContext();
  const { t } = useTranslation();

  const [allDeletedHarmReports, setAllDeletedHarmReports] = useState<
    DeletedHarmReport[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [selectedDeletedHarmReport, setSelectedDeletedHarmReport] =
    useState<DeletedHarmReport | null>(null);

  const [filters, setFilters] = useState<DeletedHarmReportsFilter>({
    searchQuery: '',
    selectedDate: new Date(),
  });

  const [openFilterModal, setOpenFilterModal] = useState(false);

  const reportService: ReportService = useMemo(() => new ReportService(), []);

  const deletedHarmReports = useMemo(() => {
    const searchQuery = filters.searchQuery?.toLowerCase();

    if (!searchQuery) return allDeletedHarmReports;

    return allDeletedHarmReports.filter(
      (report) =>
        report.reportRefNo?.toLowerCase().includes(searchQuery) ||
        report.playerProfile.id?.toLowerCase().includes(searchQuery) ||
        report.deletedBy?.firstName?.toLowerCase().includes(searchQuery) ||
        report.deletedBy?.lastName?.toLowerCase().includes(searchQuery),
    );
  }, [allDeletedHarmReports, filters.searchQuery]);

  const fetchDeletedHarmReports = useCallback(
    async (override?: Partial<HarmReportsFilter>) => {
      if (!selectedVenueBranch) return;
      const filtersToUse = { ...filters, ...override };
      setLoading(true);
      try {
        const query: GetReportsByFilterRequest = {
          venueBranchId: selectedVenueBranch._id,
          currentDate: filtersToUse.selectedDate,
          status: [ReportStatus.DELETED],
        };
        const response: GetHarmReportsResponse =
          await reportService.getHarmReports(query);
        const deletedHarmReport: DeletedHarmReport[] = (response.data
          ?.harmReports ?? []) as DeletedHarmReport[];
        setAllDeletedHarmReports(deletedHarmReport);
      } catch (error) {
        console.error('Error fetching deleted harm reports:', error);
        toastV2.error('Failed to fetch deleted harm reports.');
      } finally {
        setLoading(false);
      }
    },
    [selectedVenueBranch],
  );

  useEffect(() => {
    fetchDeletedHarmReports();
  }, [selectedVenueBranch]);

  const handleOnChangeSearch = (query: string) => {
    setFilters((f) => ({ ...f, searchQuery: query }));
  };

  const toggleFilterModal = (value: boolean) => {
    setOpenFilterModal(value);
  };

  const handleClearFilters = () => {
    const reset = {
      searchQuery: '',
      selectedDate: new Date(),
    };
    setFilters(reset);
    fetchDeletedHarmReports();
  };

  const handleApplyFilters = (newFilters: Partial<HarmReportsFilter>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    fetchDeletedHarmReports();
    setOpenFilterModal(false);
  };
  const handleDateApply = (date: Date) => {
    const normalizedDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );
    console.log('Selected date', {
      original: date,
      normalized: normalizedDate,
    });
    setFilters((prev) => ({ ...prev, selectedDate: normalizedDate }));
    fetchDeletedHarmReports({ selectedDate: normalizedDate });
  };

  const handleRowClick = (row: DeletedHarmReport) => {
    setSelectedDeletedHarmReport(row);
  };

  const closeDeletedHarmReportDetails = () => {
    setSelectedDeletedHarmReport(null);
  };

  const handleKebabAction = async (
    action: DeletedHarmReportsKebabOption,
    row: DeletedHarmReport,
  ): Promise<void> => {
    switch (action) {
      case DeletedHarmReportsKebabOption.VIEW:
        setSelectedDeletedHarmReport(row);
        return;
      case DeletedHarmReportsKebabOption.RESTORE:
        // TODO: Implement restore functionality
        toastV2.warning('Restore feature not yet available');
        return;
      default:
        console.error('Invalid Deleted Harm Report Kebab Option');
    }
  };

  const handleGoBackClick = () => {
    navigate(-1);
  };

  return {
    deletedHarmReports,
    loading,
    filters,
    selectedDeletedHarmReport,
    openFilterModal,
    handleOnChangeSearch,
    handleRowClick,
    closeDeletedHarmReportDetails,
    handleKebabAction,
    fetchDeletedHarmReports,
    toggleFilterModal,
    handleClearFilters,
    handleApplyFilters,
    handleDateApply,
    t,
    handleGoBackClick,
  };
};
